import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { Avatar } from '@components/avatar';
import { Loader } from '@components/loader';
import { PersonCard } from '@components/scheduling/personCard';
import { Schedule } from '@root/globalTypes';
import { DEFAULT_FILTERS } from '../constants';
import { hasConflict, matchesSearch, overlaps } from '../helpers';
import { FilterChips } from '../filterChips';
import { PhoneSection } from '../section';
import { ConflictLink } from '../conflictLink';
import { getConflicts, getScheduled } from '../multiDay/helpers';
import { PhoneFieldUserProps } from './types';

export const PhoneFieldUser = ({
  records,
  withoutTeam,
  loading,
  activeSchedule,
  window,
  specialty,
  disabled,
  onAssign,
  multi,
}: PhoneFieldUserProps) => {
  const { t } = useTranslation('app');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const activeForemanId = activeSchedule?.foreman?.id || '';
  const unassignedSelected = Boolean(activeSchedule) && !activeForemanId;
  const others = (list: Schedule[] = []) => list.filter((item) => item.id !== activeSchedule?.id);
  const conflictsOf = (id: string) => (multi ? getConflicts(multi.lookup, 'users', id) : []);
  const isBusy = (id: string, schedules: Schedule[]) =>
    multi ? conflictsOf(id).length > 0 : overlaps(window, schedules);
  const hasSchedule = (id: string, schedules: Schedule[]) =>
    multi ? getScheduled(multi.lookup, 'users', id).length > 0 : schedules.length > 0;

  const visible = records.filter((record) => {
    if (record.id === activeForemanId) return true;
    if (collapsed) return false;
    const schedules = others(record.schedules);
    if (filters.qualified && specialty && !record.specialties.some((el) => el.value === specialty))
      return false;
    if (filters.available && isBusy(record.id, schedules)) return false;
    if (filters.onSchedule && !hasSchedule(record.id, schedules)) return false;
    return matchesSearch(record.fullName, search);
  });
  const showUnassigned = collapsed ? unassignedSelected : unassignedSelected || !search.trim();

  return (
    <PhoneSection
      title={t('scheduling.phone.fieldUser')}
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}>
      {!collapsed && !disabled && (
        <>
          <FilterChips value={filters} onChange={setFilters} />
          <label className="flex h-10 items-center gap-2 rounded-md border-[1.5px] border-neutral-300 bg-white px-2.5 focus-within:border-primary-500">
            <Search className="h-4 w-4 shrink-0 text-neutral-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('schedule.crew.searchPlaceholder')}
              className="min-w-0 flex-1 bg-transparent text-base text-neutral-800 outline-none placeholder:text-neutral-400"
            />
          </label>
        </>
      )}
      <div
        role="radiogroup"
        aria-label={t('scheduling.phone.fieldUser')}
        aria-busy={loading}
        className="relative flex min-h-16 flex-col gap-2">
        {loading ? (
          <Loader local />
        ) : (
          <>
            {showUnassigned && (
              <PersonCard
                name={t('scheduling.phone.unassigned')}
                subtitle={t('scheduling.phone.unassignedHint')}
                avatar={<Avatar hideTooltip name="" className="h-9 w-9 bg-neutral-100" />}
                selected={unassignedSelected}
                disabled={disabled}
                schedules={multi || collapsed ? [] : others(withoutTeam)}
                currentSchedule={collapsed ? undefined : activeSchedule}
                onSelect={() => onAssign(undefined)}
              />
            )}
            {visible.map((record) => {
              const selected = activeForemanId === record.id;
              const schedules = multi ? [] : others(record.schedules);
              const ribbons = collapsed ? [] : schedules;
              const hits = conflictsOf(record.id);
              return (
                <PersonCard
                  key={record.id}
                  name={record.fullName}
                  subtitle={record.specialties.map((el) => el.name).join(', ')}
                  avatar={<Avatar hideTooltip name={record.fullName} className="h-9 w-9" />}
                  selected={selected}
                  conflict={
                    selected && (multi ? hits.length > 0 : hasConflict(activeSchedule, schedules))
                  }
                  disabled={disabled || Boolean(record.discardedAt)}
                  schedules={ribbons}
                  currentSchedule={collapsed ? undefined : activeSchedule}
                  onSelect={() => onAssign(record)}
                  nameAddon={
                    multi &&
                    hits.length > 0 && (
                      <ConflictLink
                        count={new Set(hits.map((hit) => hit.date)).size}
                        onClick={() => multi.onShowConflicts(record.fullName, hits)}
                      />
                    )
                  }
                />
              );
            })}
            {!visible.length && !showUnassigned && (
              <p className="py-2 text-sm text-neutral-500">{t('scheduling.phone.noResults')}</p>
            )}
          </>
        )}
      </div>
    </PhoneSection>
  );
};
